import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';

// ── Customer details (Phase 1 slice D) ──
//
// Shown in the expanded customer card on the Customers tab: contact, billing
// and shipping addresses, payment terms, price tier, tax code and sales rep,
// read live from SOS. "Edit" turns the same card into a form; Preview shows
// what changes (and, for a tier move, what their usual items will cost);
// Save writes through to SOS. Terms, tier and tax code are owner/admin only.
// The rep is read-only here — it's set on the sales site.

const ADDRESS_FIELDS = [
  ['line1', 'Street'], ['line2', 'Line 2'], ['city', 'City'],
  ['stateProvince', 'State'], ['postalCode', 'ZIP'], ['country', 'Country'],
];

const box = { border: '1px solid #eee', borderRadius: 8, padding: '0.75rem 0.9rem', background: '#fff', marginTop: '0.75rem' };
const label = { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.45)', marginBottom: 4 };
const input = { width: '100%', boxSizing: 'border-box', border: '1px solid #ddd', borderRadius: 6, padding: '0.4rem 0.55rem', fontSize: '0.85rem', fontFamily: 'inherit' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.9rem' };
const muted = { color: 'rgba(0,0,0,0.45)' };
const money = n => `$${Number(n).toFixed(2)}`;

function formatAddress(a) {
  const street = [a.line1, a.line2].filter(Boolean).join(', ');
  const place = [a.city, [a.stateProvince, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, place, a.country].filter(Boolean);
}

function nameOf(list, id, fallback) {
  if (id == null) return fallback;
  return list.find(x => Number(x.id) === Number(id))?.name || `#${id}`;
}

export default function CustomerDetailsEditor({ customer, readOnly }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const r = await adminApi.getCustomerDetails(customer.id);
      setData(r);
    } catch (e) {
      setLoadError(e.message);
    }
  }, [customer.id]);

  useEffect(() => { load(); }, [load]);

  if (loadError) return <div style={{ ...box, color: '#A50B18', fontSize: '0.85rem' }}>Couldn't load details from SOS: {loadError}</div>;
  if (!data) return <div style={{ ...box, ...muted, fontSize: '0.85rem' }}>Loading details from SOS…</div>;

  const c = data.customer;
  const { lists, canEditMoney } = data;
  const taxCode = lists.taxCodes.find(t => Number(t.id) === Number(c.taxCodeId));

  function startEdit() {
    setForm(JSON.parse(JSON.stringify({
      contact: c.contact, email: c.email, phone: c.phone, billing: c.billing, shipping: c.shipping,
      termsId: c.termsId, priceTierId: c.priceTierId, taxCodeId: c.taxCodeId,
    })));
    setPreview(null);
    setError('');
    setReason('');
    setSaved('');
  }

  function update(path, value) {
    setForm(f => {
      const next = { ...f };
      const [top, sub] = path.split('.');
      if (sub) next[top] = { ...next[top], [sub]: value };
      else next[top] = value;
      return next;
    });
    setPreview(null);
    setError('');
  }

  function payload(extra) {
    const body = { contact: form.contact, email: form.email, phone: form.phone, billing: form.billing, shipping: form.shipping, ...extra };
    // Only send the money fields when this person may change them, so a sales
    // rep's contact edit never trips the owner/admin check.
    if (canEditMoney) Object.assign(body, { termsId: form.termsId, priceTierId: form.priceTierId });
    if (canEditMoney && !c.taxExempt && form.taxCodeId != null) body.taxCodeId = form.taxCodeId;
    return body;
  }

  async function runPreview() {
    setBusy(true);
    setError('');
    try {
      const r = await adminApi.setCustomerDetails(customer.id, payload({ dryRun: true }));
      setPreview(r.preview);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Whether SOS now holds what this form sent — used when the browser gave up
  // waiting. A slow SOS can take longer than the 29-second request limit while
  // the save still completes on the server.
  function matchesForm(saved) {
    const same = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
    const fields = [
      [saved.contact.firstName, form.contact.firstName], [saved.contact.lastName, form.contact.lastName],
      [saved.email, form.email], [saved.phone, form.phone],
      ...['billing', 'shipping'].flatMap(k => ADDRESS_FIELDS.map(([f]) => [saved[k][f], form[k][f]])),
    ];
    if (canEditMoney) fields.push([saved.termsId, form.termsId], [saved.priceTierId, form.priceTierId]);
    if (canEditMoney && !c.taxExempt && form.taxCodeId != null) fields.push([saved.taxCodeId, form.taxCodeId]);
    return fields.every(([a, b]) => same(a, b));
  }

  async function save() {
    setBusy(true);
    setError('');
    try {
      await adminApi.setCustomerDetails(customer.id, payload({ reason }));
      setForm(null);
      setPreview(null);
      setSaved('Saved to SOS.');
      await load();
    } catch (e) {
      if (e instanceof TypeError) {
        // The server keeps saving after the browser gives up; SOS sometimes
        // stalls for a minute or more per request, so keep looking for the
        // change for up to three minutes before saying we couldn't see it.
        const started = Date.now();
        while (Date.now() - started < 3 * 60 * 1000) {
          const waited = Math.round((Date.now() - started) / 1000);
          setError(`SOS is slow to answer — still checking whether the change went through (${waited}s)…`);
          try {
            const r = await adminApi.getCustomerDetails(customer.id);
            if (matchesForm(r.customer)) {
              setData(r);
              setForm(null);
              setPreview(null);
              setError('');
              setSaved('Saved to SOS (SOS was slow to confirm).');
              return;
            }
          } catch (_) { /* SOS stalled again — try once more */ }
          await new Promise(resolve => setTimeout(resolve, 10000));
        }
        setError('SOS still isn\'t showing the change after 3 minutes. Close and reopen this customer later to check before saving again.');
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Customer details <span style={{ ...muted, fontWeight: 400, fontSize: '0.78rem' }}>— from SOS</span></div>
      {!readOnly && !form && (
        <button type="button" onClick={startEdit} style={{ background: 'none', border: '1px solid #A50B18', color: '#A50B18', borderRadius: 6, padding: '0.3rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
          Edit
        </button>
      )}
    </div>
  );

  if (!form) {
    return (
      <div style={box} onClick={e => e.stopPropagation()}>
        {header}
        {c.archived && <div style={{ color: '#A50B18', fontSize: '0.82rem', marginBottom: 6 }}>Archived in SOS</div>}
        <div style={{ ...grid, fontSize: '0.85rem', lineHeight: 1.45 }}>
          <div>
            <div style={label}>Contact</div>
            <div>{[c.contact.firstName, c.contact.lastName].filter(Boolean).join(' ') || <span style={muted}>—</span>}</div>
            <div>{c.email || <span style={muted}>no email</span>}</div>
            <div>{c.phone || <span style={muted}>no phone</span>}</div>
          </div>
          <div>
            <div style={label}>Billing address</div>
            {formatAddress(c.billing).map(l => <div key={l}>{l}</div>)}
            {formatAddress(c.billing).length === 0 && <span style={muted}>—</span>}
          </div>
          <div>
            <div style={label}>Shipping address</div>
            {formatAddress(c.shipping).map(l => <div key={l}>{l}</div>)}
            {formatAddress(c.shipping).length === 0 && <span style={muted}>—</span>}
          </div>
          <div>
            <div style={label}>Account</div>
            <div>Terms: {nameOf(lists.terms, c.termsId, 'None')}</div>
            <div>Price tier: {nameOf(lists.tiers, c.priceTierId, 'List price')}</div>
            <div>Tax: {c.taxExempt ? 'Exempt (set in SOS)' : taxCode ? `${taxCode.name} (${taxCode.rate}%)` : nameOf([], c.taxCodeId, 'None')}</div>
            <div>Sales rep: {c.salesRepName || <span style={muted}>none</span>}</div>
          </div>
        </div>
        {saved && <div style={{ color: '#2e7d32', fontSize: '0.82rem', marginTop: 8 }}>{saved}</div>}
      </div>
    );
  }

  const field = (path, text, props = {}) => {
    const [top, sub] = path.split('.');
    const value = sub ? form[top]?.[sub] ?? '' : form[top] ?? '';
    return (
      <label style={{ display: 'block', marginBottom: 6 }}>
        <div style={{ ...muted, fontSize: '0.75rem' }}>{text}</div>
        <input style={input} value={value} onChange={e => update(path, e.target.value)} {...props} />
      </label>
    );
  };
  const select = (key, text, options, emptyLabel) => (
    <label style={{ display: 'block', marginBottom: 6 }}>
      <div style={{ ...muted, fontSize: '0.75rem' }}>{text}</div>
      <select
        style={{ ...input, background: canEditMoney ? '#fff' : '#f6f6f6' }}
        value={form[key] ?? ''}
        disabled={!canEditMoney}
        onChange={e => update(key, e.target.value === '' ? null : Number(e.target.value))}
      >
        {emptyLabel != null && <option value="">{emptyLabel}</option>}
        {options}
      </select>
    </label>
  );
  const taxOptions = [...lists.taxCodes]
    .sort((a, b) => a.name.localeCompare(b.name) || b.customers - a.customers)
    .map(t => <option key={t.id} value={t.id}>{t.name} — {t.rate}% ({t.customers} customer{t.customers === 1 ? '' : 's'})</option>);

  return (
    <div style={box} onClick={e => e.stopPropagation()}>
      {header}
      <div style={grid}>
        <div>
          <div style={label}>Contact</div>
          {field('contact.firstName', 'First name', { maxLength: 60 })}
          {field('contact.lastName', 'Last name', { maxLength: 60 })}
          {field('email', 'Email (SOS / QuickBooks — not the portal login)', { maxLength: 120, type: 'email' })}
          {field('phone', 'Phone', { maxLength: 40 })}
        </div>
        {['billing', 'shipping'].map(kind => (
          <div key={kind}>
            <div style={label}>{kind === 'billing' ? 'Billing address' : 'Shipping address'}</div>
            {ADDRESS_FIELDS.map(([f, t]) => <div key={f}>{field(`${kind}.${f}`, t, { maxLength: 100 })}</div>)}
          </div>
        ))}
        <div>
          <div style={label}>Account</div>
          {select('termsId', 'Payment terms', lists.terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>), 'None')}
          {select('priceTierId', 'Price tier', lists.tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>), 'List price (no tier)')}
          {c.taxExempt
            ? <div style={{ marginBottom: 6 }}><div style={{ ...muted, fontSize: '0.75rem' }}>Tax code</div><div>Exempt — change in SOS</div></div>
            : select('taxCodeId', 'Tax code', taxOptions, form.taxCodeId == null ? 'None' : null)}
          {!canEditMoney && <div style={{ ...muted, fontSize: '0.75rem' }}>Terms, tier and tax code: owners and admins only.</div>}
          <div style={{ ...muted, fontSize: '0.75rem', marginTop: 6 }}>Sales rep: {c.salesRepName || 'none'} — set on the sales site.</div>
        </div>
      </div>

      <label style={{ display: 'block', marginTop: 8 }}>
        <div style={{ ...muted, fontSize: '0.75rem' }}>Reason (optional)</div>
        <input style={input} value={reason} onChange={e => setReason(e.target.value)} maxLength={300} placeholder="e.g. moved to the loading dock" />
      </label>

      {preview && (
        <div style={{ marginTop: '0.8rem', padding: '0.7rem 0.85rem', background: '#faf8f6', border: '1px solid #eee', borderRadius: 6, fontSize: '0.85rem', lineHeight: 1.5 }}>
          {preview.noChange && <div>Nothing to change — SOS already has these details.</div>}
          {preview.changes.map(ch => (
            <div key={ch.field}><strong>{ch.label}:</strong> {ch.fromLabel} → {ch.toLabel}</div>
          ))}
          {preview.tierImpact && (
            <div style={{ marginTop: 6 }}>
              {preview.tierImpact.changed === 0
                ? <span style={muted}>None of the {preview.tierImpact.considered} items they bought in the last year change price.</span>
                : <>
                  <div>{preview.tierImpact.changed} of the {preview.tierImpact.considered} items they bought in the last year change price:</div>
                  {preview.tierImpact.examples.map(x => (
                    <div key={x.sosId} style={{ ...muted, paddingLeft: 10 }}>
                      {x.name}: {money(x.from)} → <span style={{ color: x.to > x.from ? '#A50B18' : '#2e7d32' }}>{money(x.to)}</span>
                    </div>
                  ))}
                </>}
            </div>
          )}
          {preview.notes.map(n => <div key={n} style={{ ...muted, marginTop: 4 }}>{n}</div>)}
          {preview.blockers.map(b => <div key={b} style={{ color: '#A50B18', fontWeight: 600, marginTop: 4 }}>✕ {b}</div>)}
        </div>
      )}

      {error && <div style={{ color: '#A50B18', fontSize: '0.85rem', marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
        <button type="button" disabled={busy} onClick={() => { setForm(null); setPreview(null); setError(''); }} style={{ background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
          Cancel
        </button>
        {!preview ? (
          <button type="button" disabled={busy} onClick={runPreview} style={{ background: '#333', color: '#fff', border: 'none', borderRadius: 6, padding: '0.5rem 1.1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            {busy ? 'Checking…' : 'Preview'}
          </button>
        ) : (
          <button
            type="button"
            disabled={busy || preview.noChange || preview.blockers.length > 0}
            onClick={save}
            style={{ background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.5rem 1.1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', opacity: (preview.noChange || preview.blockers.length) ? 0.5 : 1 }}
          >
            {busy ? 'Saving to SOS…' : 'Save to SOS'}
          </button>
        )}
      </div>
    </div>
  );
}
